import type { Scene, ComponentStore, Entity } from '../engine/core/Scene';
import type { Transform } from '../engine/core/Transform';
import type { Input } from '../engine/input/Input';
import type { Camera } from '../engine/render/Camera';
import type { Renderer, FrameParams } from '../engine/render/Renderer';
import type { GpuMesh } from '../engine/render/GpuMesh';
import type { StaticWorld } from '../engine/physics/StaticWorld';
import type { TriggerSystem } from '../engine/physics/Triggers';
import type { Tweens } from '../engine/anim/Tween';
import type { MeshData } from '../engine/geo/mesh';
import type { Sfx } from './audio/Sfx';

/** A mesh attached to a transform, plus material knobs. */
export interface Renderable {
  mesh: GpuMesh;
  emissiveMul: number;
  flash: number;
  alpha: number;
  wind: number;
  castShadow: boolean;
  transparent: boolean;
  visible: boolean;
  instanced: boolean;
  noCull: boolean;
  ambientMul: number;
}

export function makeRenderable(mesh: GpuMesh, opts: Partial<Renderable> = {}): Renderable {
  return { mesh, emissiveMul: 1, flash: 0, alpha: 1, wind: 0, castShadow: true, transparent: false, visible: true, instanced: false, noCull: false, ambientMul: 1, ...opts };
}

/** Everything a game system needs. Game implements this. */
export interface Ctx {
  scene: Scene;
  transforms: ComponentStore<Transform>;
  renderables: ComponentStore<Renderable>;
  statics: StaticWorld;
  triggers: TriggerSystem;
  tweens: Tweens;
  input: Input;
  camera: Camera;
  renderer: Renderer;
  frame: FrameParams;
  sfx: Sfx;
  /** Simulated seconds since the run started. */
  time: number;
  /** Add an entity with a transform and optional mesh. */
  spawn(name: string, t: Transform, mesh?: MeshData | GpuMesh, opts?: Partial<Renderable>): Entity;
  /** Camera shake / fov kicks etc. */
  fx: {
    shake(amount: number, duration?: number): void;
    fovKick(delta: number, duration?: number): void;
    hitStop(seconds: number): void;
    flashWhite(amount: number): void;
  };
}
